import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiQueryResult } from '@euler/model/bi/result';
import {
  BiAxisSide,
  BiChartViz,
  BiLegendPosition,
  BiPalette,
  BiVizType,
} from '@euler/model/bi/viz';
import { memo, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { BiVizMismatch } from './BiVizMismatch';
import { BiCartesianChart } from './chart/BiCartesianChart';
import { BiChartLegend, BiLegendItem } from './chart/BiChartLegend';
import { BiPieChart, biShareText } from './chart/BiPieChart';
import { BiPyramidChart } from './chart/BiPyramidChart';
import { BiRadarChart } from './chart/BiRadarChart';
import { BiChartModel, biChartModel } from './functions/biChartModel';
import { biVizLook } from './functions/biVizLook';

/** What the legend lists: series, or a pie's slices with their values. */
function legendItems(model: BiChartModel, percent: boolean): BiLegendItem[] {
  if (model.kind === 'pie') {
    const sum = model.slices.reduce((total, slice) => total + slice.value, 0);
    return model.slices.map(slice => ({
      name: slice.name,
      color: slice.color,
      note: percent
        ? `${slice.label}，${biShareText(slice.value, sum)}`
        : slice.label,
    }));
  }
  const right = model.series.some(entry => entry.axis === BiAxisSide.Right);
  return model.series.map(entry => ({
    name: entry.name,
    color: entry.color,
    type: entry.type,
    note: right && entry.axis === BiAxisSide.Right ? '右轴' : undefined,
  }));
}

function unitText(model: BiChartModel): string | null {
  if (model.kind === 'pie') return null;
  if (model.kind === 'radar' && model.perSeries)
    return '各指标按各自最大值缩放';
  const { left, right } = model.units;
  if (left && right) return `单位：${left}（左） · ${right}（右）`;
  if (right) return `右轴单位：${right}`;
  return left ? `单位：${left}` : null;
}

/** A chart viz, drawn at the width it is given and styled as it says. */
export const BiChartView = memo(
  ({
    viz,
    result,
    palette,
    height,
    fill,
  }: {
    viz: BiChartViz;
    result: BiQueryResult;
    /** The dashboard's palette, for cards that do not set their own. */
    palette?: BiPalette;
    /** The plot's height; a default that fits a chat bubble or a card. */
    height?: number;
    /**
     * The plot takes the height left in a fixed-size container (a dashboard
     * card) once the caption and legend are placed, so nothing spills into
     * the card's margin.
     */
    fill?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const model = useMemo(
      () => biChartModel(viz, result, palette),
      [viz, result, palette],
    );
    const look = useMemo(() => biVizLook(viz), [viz]);
    const [box, setBox] = useState<{ width: number; height: number } | null>(
      null,
    );
    const width = box?.width ?? null;
    if (!model) return <BiVizMismatch />;
    const legend =
      look.legend.show &&
      (look.legendSet || model.kind === 'pie' || model.legend)
        ? legendItems(model, model.kind === 'pie' && look.pie.showPercent)
        : null;
    const side = legend && look.legend.position === BiLegendPosition.Right;
    const unit = unitText(model);
    const hidden =
      model.kind === 'radar' || model.kind === 'pyramid' ? model.hidden : 0;
    // Bars that set their own height (a row each) keep it: in a fixed-size
    // card the whole chart sits centered, and scrolls when it is taller.
    const ownHeight =
      model.kind === 'pyramid' ||
      (model.kind === 'cartesian' && model.type === BiVizType.HorizontalBar);
    const scrolls = !!fill && ownHeight;
    const grows = !!fill && !ownHeight;
    const chartHeight = grows && box ? box.height : height ?? rpx(200);
    const chart =
      width == null ? null : model.kind === 'pie' ? (
        <BiPieChart
          model={model}
          look={look}
          size={Math.min(width, chartHeight, rpx(220))}
        />
      ) : model.kind === 'radar' ? (
        <BiRadarChart
          model={model}
          look={look}
          width={width}
          height={fill ? chartHeight : Math.max(chartHeight, rpx(220))}
        />
      ) : model.kind === 'pyramid' ? (
        <BiPyramidChart model={model} look={look} width={width} />
      ) : (
        <BiCartesianChart
          model={model}
          look={look}
          width={width}
          height={chartHeight}
        />
      );
    const legendView = legend ? (
      <BiChartLegend items={legend} vertical={!!side} />
    ) : null;
    const content = (
      <>
        {unit ? (
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {unit}
          </Typography.Text>
        ) : null}
        {hidden > 0 ? (
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {`另有 ${hidden} 组未画出，完整数据见数据表`}
          </Typography.Text>
        ) : null}
        {legend && look.legend.position === BiLegendPosition.Top
          ? legendView
          : null}
        <View
          style={[
            side && {
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: rpx(12),
            },
            grows && { flex: 1 },
          ]}
        >
          <View
            style={[
              (side || grows) && { flex: 1 },
              side && grows && { alignSelf: 'stretch' },
            ]}
            onLayout={event => {
              const { width: w, height: h } = event.nativeEvent.layout;
              setBox(previous =>
                previous?.width === w && previous.height === h
                  ? previous
                  : { width: w, height: h },
              );
            }}
          >
            {chart}
          </View>
          {side ? legendView : null}
        </View>
        {legend && look.legend.position === BiLegendPosition.Bottom
          ? legendView
          : null}
      </>
    );
    return scrolls ? (
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          gap: rpx(8),
        }}
        nestedScrollEnabled
      >
        {content}
      </ScrollView>
    ) : (
      <View style={{ gap: rpx(8), flex: grows ? 1 : undefined }}>
        {content}
      </View>
    );
  },
);
